import { useEffect, useRef, useState } from "react";
import {
  Collapse,
  Space,
  Tag,
  Typography,
  Card,
  Empty,
  Modal,
  Button,
  message,
  Input,
  Spin,
  Select,
  Table,
  FloatButton,
  notification,
  List,
  Avatar,
  Popconfirm,
} from "antd";
import {
  BASE_URL_BEN,
  HEADERS_EXPORT_BEN,
  POST_SEND_RESPONE_INVESTIGATE,
  POST_UPDATE_EXCEL_MEMO,
} from "../../../API/apiUrls";
import axios from "axios";
import { TYPE_OPTIONS } from "../constant/landType";
import dayjs from "dayjs";
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

// ======================== Helpers ========================
const fullName = (p) => [p?.SNAM, p?.NAME1, p?.NAME2].filter(Boolean).join(" ");
const generateAssetKey = () =>
  `assetLand-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ======================== Main Component ========================
const AddRelationAssets = ({
  open,
  close,
  data = [],
  defaultData,
  geoRawData,
  func,
  sumMoney,
  sumMoneyEstimate,
}) => {
  const USERNAME = localStorage.getItem("USERNAME");
  const userCompany = localStorage.getItem("COMPANY_ID");

  const [loading, setLoading] = useState(false);
  const [selectedMap, setSelectedMap] = useState({});
  const [preData, setPreData] = useState(data); // working copy
  const [changedKeys, setChangedKeys] = useState(new Set());
  const [changedKeysRelation, setChangedKeysRelation] = useState(new Set());
  const panelMapRef = useRef({});
  const scrollRef = useRef(null);
  const { Text } = Typography;

  const [failedModal, setFailedModal] = useState({ open: false, items: [] });

  // คุมเปิด/ปิด collapse (คลิก label เพื่อ toggle)
  const [activeKey, setActiveKey] = useState();
  const toggleItem = (k) =>
    setActiveKey((prev) => (prev === k ? undefined : k));

  // เรียงรายการตามเกณฑ์ที่กำหนด (และตรึง order เดิม)
  useEffect(() => {
    setPreData(
      [...data].sort((a, b) => {
        const aHasErrors = a.errors?.length > 0;
        const bHasErrors = b.errors?.length > 0;
        const aHasCustomerId = a.customerId > 0;
        const bHasCustomerId = b.customerId > 0;
        if (aHasErrors && !bHasErrors) return -1;
        if (!aHasErrors && bHasErrors) return 1;
        if (!aHasErrors && !aHasCustomerId && !bHasErrors && bHasCustomerId)
          return -1;
        if (!bHasErrors && !bHasCustomerId && !aHasErrors && aHasCustomerId)
          return 1;
        if (!aHasCustomerId && bHasCustomerId) return -1;
        if (aHasCustomerId && !bHasCustomerId) return 1;
        return 0;
      }),
    );
  }, [data]);

  // ===================== Normalize & find codes =====================
  const normalizeText = (text) =>
    (text || "")
      .replace(/^(จ\.|จังหวัด|อ\.|อำเภอ|เขต|ต\.|ตำบล|แขวง|ห้องชุดเลขที่)/g, "")
      .trim()
      .replace(/์/g, "")
      .replace(/\s+/g, "")
      .toLowerCase();

  const findProvinceByName = (name) => {
    const n = normalizeText(name);
    return (
      geoRawData.province?.find((p) => normalizeText(p.provDesc) === n) || null
    );
  };

  const findDistrictByName = (provCode, name) => {
    const n = normalizeText(name);
    return (
      geoRawData.district?.find(
        (d) =>
          String(d.provCode) === String(provCode) &&
          normalizeText(d.distDesc) === n,
      ) || null
    );
  };

  const findSubDistrictByName = (provCode, distCode, name) => {
    const n = normalizeText(name);
    return (
      geoRawData.subDistrict?.find(
        (s) =>
          String(s.provCode) === String(provCode) &&
          String(s.distCode) === String(distCode) &&
          normalizeText(s.subDistDesc) === n,
      ) || null
    );
  };

  // ===================== อัปเดต assetLand: ระดับแถว =====================
  const updateAssetRow = (assetLand, field, value) => {
    let next = { ...assetLand };

    if (field === "typeLand") {
      next.typeLand = value;
      return next;
    }

    if (field === "numberLand") {
      next.numberLand = value;
      return next;
    }

    if (field === "provinceName") {
      const pv = findProvinceByName(value);
      next.province = pv?.provCode?.toString() || null;
      next.provinceName = pv?.provDesc || value || null;
      next.district = null;
      next.districtName = null;
      next.subdistrict = null;
      next.subdistrictName = null;
      return next;
    }

    if (field === "districtName") {
      const pvCode =
        assetLand.province ||
        findProvinceByName(assetLand.provinceName)?.provCode;
      const dt = pvCode ? findDistrictByName(pvCode, value) : null;

      next.district = dt?.distCode || null;
      next.districtName = dt?.distDesc || value || null;
      next.subdistrict = null;
      next.subdistrictName = null;
      return next;
    }

    if (field === "subdistrictName") {
      const pvCode =
        assetLand.province ||
        findProvinceByName(assetLand.provinceName)?.provCode;
      const dtCode =
        assetLand.district ||
        (pvCode
          ? findDistrictByName(pvCode, assetLand.districtName)?.distCode
          : null);

      const sb =
        pvCode && dtCode ? findSubDistrictByName(pvCode, dtCode, value) : null;

      next.subdistrict = sb?.subDistCode || null;
      next.subdistrictName = sb?.subDistDesc || value || null;
      return next;
    }

    return next;
  };

  // ===================== handle edit แถวใน assetLand =====================
  const handleAssetEdit = (itemKey, rowIndex, field, value) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) return;

    setPreData((prev) =>
      prev.map((itm) => {
        if (itm.key !== owner.key) return itm;

        const newAssetLand = (itm.assetLand || []).map((row, idx) =>
          idx === rowIndex ? updateAssetRow(row, field, value) : row,
        );

        return {
          ...itm,
          assetLand: newAssetLand,
          // มีการแก้ไข -> ล้าง errors ของ item นี้
          errors: [],
        };
      }),
    );

    setChangedKeys((prev) => new Set([...prev, owner.key]));
  };

  // ===================== เพิ่ม/ลบแถวใน assetLand =====================
  const handleAddAssetRow = (itemKey) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) return;

    const newRow = {
      key: generateAssetKey(),
      typeLand: 1,
      numberLand: "",
      province: null,
      provinceName: null,
      district: null,
      districtName: null,
      subdistrict: null,
      subdistrictName: null,
    };

    setPreData((prev) =>
      prev.map((itm) =>
        itm.key === owner.key
          ? {
              ...itm,
              assetLand: [...(itm.assetLand || []), newRow],
              investigation: true,
              errors: [],
            }
          : itm,
      ),
    );

    setChangedKeys((prev) => new Set([...prev, owner.key]));
    setActiveKey(itemKey);
  };

  const handleDeleteAssetRow = (itemKey, rowIndex) => {
    const owner = panelMapRef.current[itemKey];
    if (!owner) return;

    setPreData((prev) =>
      prev.map((itm) => {
        if (itm.key !== owner.key) return itm;

        const newAssetLand = (itm.assetLand || []).filter(
          (_, idx) => idx !== rowIndex,
        );

        return {
          ...itm,
          assetLand: newAssetLand,
          investigation: newAssetLand.length > 0,
          errors: [],
        };
      }),
    );

    setChangedKeys((prev) => new Set([...prev, owner.key]));
    message.success("ลบทรัพย์เรียบร้อย");
  };

  // ===================== columns (แก้ไขได้ทุกคอลัมน์) =====================
  const makeAssetColumns = (itemKey) => [
    {
      title: "ประเภท",
      dataIndex: "typeLand",
      key: "typeLand",
      width: 120,
      render: (val, record, rowIndex) => (
        <Select
          size="small"
          style={{ width: "100%" }}
          options={TYPE_OPTIONS}
          value={val}
          onChange={(v) => handleAssetEdit(itemKey, rowIndex, "typeLand", v)}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "เลขที่ดิน",
      dataIndex: "numberLand",
      key: "numberLand",
      width: 160,
      render: (val, record, rowIndex) => (
        <Input
          size="small"
          value={val}
          onChange={(e) =>
            handleAssetEdit(itemKey, rowIndex, "numberLand", e.target.value)
          }
          placeholder="เช่น 554, 12/32"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "จังหวัด",
      dataIndex: "provinceName",
      key: "provinceName",
      width: 180,
      render: (val, record, rowIndex) => (
        <Select
          showSearch
          size="small"
          optionFilterProp="label"
          popupMatchSelectWidth={false}
          style={{ width: "100%" }}
          placeholder="เลือกจังหวัด"
          value={record.province ? record.provinceName : undefined}
          options={
            geoRawData.province?.map((p) => ({
              label: p.provDesc,
              value: p.provDesc,
            })) || []
          }
          onChange={(v) =>
            handleAssetEdit(itemKey, rowIndex, "provinceName", v)
          }
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: "เขต/อำเภอ",
      dataIndex: "districtName",
      key: "districtName",
      width: 200,
      render: (val, record, rowIndex) => {
        const pvCode =
          record.province ||
          findProvinceByName(record.provinceName)?.provCode ||
          null;

        const districtOpts = pvCode
          ? geoRawData.district
              ?.filter((d) => String(d.provCode) === String(pvCode))
              .map((d) => ({ label: d.distDesc, value: d.distDesc }))
          : [];

        return (
          <Select
            showSearch
            size="small"
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            style={{ width: "100%" }}
            placeholder="เลือกเขต/อำเภอ"
            value={record.district ? record.districtName : null}
            options={districtOpts}
            onChange={(v) =>
              handleAssetEdit(itemKey, rowIndex, "districtName", v)
            }
            disabled={!pvCode}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: "แขวง/ตำบล",
      dataIndex: "subdistrictName",
      key: "subdistrictName",
      width: 200,
      render: (val, record, rowIndex) => {
        const pvCode =
          record.province ||
          findProvinceByName(record.provinceName)?.provCode ||
          null;
        const dtCode =
          record.district ||
          (pvCode
            ? findDistrictByName(pvCode, record.districtName)?.distCode
            : null);

        const subOpts =
          pvCode && dtCode
            ? geoRawData.subDistrict
                ?.filter(
                  (s) =>
                    String(s.provCode) === String(pvCode) &&
                    String(s.distCode) === String(dtCode),
                )
                .map((s) => ({ label: s.subDistDesc, value: s.subDistDesc }))
            : [];

        return (
          <Select
            showSearch
            size="small"
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            style={{ width: "100%" }}
            placeholder="เลือกแขวง/ตำบล"
            value={record.subdistrict ? record.subdistrictName : null}
            options={subOpts}
            onChange={(v) =>
              handleAssetEdit(itemKey, rowIndex, "subdistrictName", v)
            }
            disabled={!pvCode || !dtCode}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, record, rowIndex) => (
        <Popconfirm
          title="ลบทรัพย์นี้?"
          okText="ลบ"
          cancelText="ยกเลิก"
          onConfirm={() => handleDeleteAssetRow(itemKey, rowIndex)}
          onCancel={(e) => e?.stopPropagation()}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  const handleCancel = () => {
    setFailedModal({ open: false });
    close?.(false);
    console.log("11111111");
  };

  // เช็คข้อมูล assetLand ของ item ที่ระบุ
  const checkAssetByIndex = (data, itemIndex) => {
    // ถ้าไม่มี itemIndex ให้เช็คทั้งหมด
    const itemsToCheck = itemIndex !== undefined ? [data[itemIndex]] : data;

    // เช็ค: ทั้ง item ต้องมี assetLand และ ทั้ง assetLand ต้องมี province และ district
    const isValid = itemsToCheck.every((item) => {
      // ถ้าไม่มี assetLand เลย ถือว่า invalid
      if (!item.assetLand || item.assetLand.length === 0) {
        return true;
      }
      // ทั้ง assetLand ต้องมี province และ district
      return item.assetLand.every(
        (assetLand) =>
          assetLand.province &&
          assetLand.district &&
          assetLand.numberLand &&
          assetLand.numberLand.trim() !== "",
      );
    });

    // นับจำนวน assetLand ที่ขาด province และ district
    const assetDetail = itemsToCheck.map((item, idx) => {
      const missingProvince = item.assetLand?.filter((a) => !a.province) || [];
      const missingDistrict = item.assetLand?.filter((a) => !a.district) || [];
      const missingNumberLand =
        item.assetLand?.filter(
          (a) => !a.numberLand || a.numberLand.trim() === "",
        ) || [];
      return {
        itemIndex: itemIndex !== undefined ? itemIndex : idx,
        data: item,
        totalAsset: item.assetLand?.length || 0,
        missingProvinceCount: missingProvince.length,
        missingDistrictCount: missingDistrict.length,
        missingNumberLandCount: missingNumberLand.length,
        isValid:
          item.assetLand &&
          item.assetLand.length > 0 &&
          item.assetLand.every(
            (a) =>
              a.province &&
              a.district &&
              a.numberLand &&
              a.numberLand.trim() !== "",
          ),
      };
    });

    return {
      isValid,
      assetDetail,
    };
  };

  // ===================== บันทึกทั้งหมด / ส่งกลับ parent =====================
  const handleSave = async () => {
    // func(true, 38);
    try {
      setLoading(true);
      const { isValid, assetDetail } = checkAssetByIndex(preData);
      console.log("assetDetail", assetDetail);

      if (!isValid) {
        const errorList = assetDetail
          .filter(
            (d) =>
              !d.isValid &&
              (d.missingProvinceCount > 0 ||
                d.missingDistrictCount > 0 ||
                d.missingNumberLandCount > 0),
          )
          .map((d, index) => (
            <li key={index}>
              ข้อมูล{" "}
              <b>
                {d.data.SNAM} {d.data.NAME1} {d.data.NAME2 || ""}
              </b>
              {d.missingNumberLandCount > 0 && (
                <div>
                  : ยังไม่ได้กรอก "เลขที่ดิน" {d.missingNumberLandCount} ตัว
                </div>
              )}
              {d.missingProvinceCount > 0 && (
                <div>
                  : ยังไม่ได้เลือก "จังหวัด" {d.missingProvinceCount} ตัว
                </div>
              )}
              {d.missingDistrictCount > 0 && (
                <div>: ยังไม่ได้เลือก "อำเภอ" {d.missingDistrictCount} ตัว</div>
              )}
            </li>
          ));

        if (errorList.length > 0) {
          notification.error({
            message: "ข้อมูลไม่ครบ",
            description: (
              <div>
                <p>กรุณาตรวจสอบข้อมูลต่อไปนี้:</p>
                <ul style={{ marginLeft: 20, paddingLeft: 10 }}>{errorList}</ul>
              </div>
            ),
            duration: 8, // แสดงนาน 8 วิ (ปรับได้)
            placement: "topRight", // ตำแหน่ง popup
          });
        }
        return;
      }
      const dataMemo = preDataMemo(preData);
      console.log("preData", preData);
      console.log("dataMemo", dataMemo);

      await postData(preData);

      let success = [];
      let failed = [];
      if (userCompany !== "3") {
        ({ success, failed } = await postMemoSequential(dataMemo, 500));
      } else {
        success = dataMemo.map((d) => d.CONTNO);
      }

      // demo dummy data
      // const { success, failed } = {
      //   success: ["2-0012214", "2-0012734"],
      //   failed: [
      //     {
      //       CONTNO: "2-0007973",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 690 บาท(พบทรัพย์ ฉ.36031 ต.ร้องกวาง อ.ร้องกวาง จ.แพร่)",
      //       error: "Network Error",
      //     },
      //     {
      //       CONTNO: "V-001077",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 900 บาท(พบทรัพย์ นส.3ก.15 ต.แม่คำมี อ.หนองม่วงไข่ จ.แพร่)",
      //       error: "Request timeout",
      //     },
      //     {
      //       CONTNO: "8-0004959",
      //       MEMO1: "ค่าสืบทรัพย์ 1,720 บาท(ไม่พบทรัพย์)",
      //       error: "500 Internal Server Error",
      //     },
      //     {
      //       CONTNO: "2-0007973",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 690 บาท(พบทรัพย์ ฉ.36031 ต.ร้องกวาง อ.ร้องกวาง จ.แพร่)",
      //       error:
      //         "SQLExecute: {22001} [IBM][CLI Driver][DB2/NT64] SQL0302N  The value of a host variable in the EXECUTE or OPEN statement is out of range for its corresponding use.  SQLSTATE=22001",
      //     },
      //     {
      //       CONTNO: "V-001077",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 900 บาท(พบทรัพย์ นส.3ก.15 ต.แม่คำมี อ.หนองม่วงไข่ จ.แพร่)",
      //       error: "Request timeout",
      //     },
      //     {
      //       CONTNO: "8-0004959",
      //       MEMO1: "ค่าสืบทรัพย์ 1,720 บาท(ไม่พบทรัพย์)",
      //       error: "500 Internal Server Error",
      //     },
      //     {
      //       CONTNO: "2-0007973",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 690 บาท(พบทรัพย์ ฉ.36031 ต.ร้องกวาง อ.ร้องกวาง จ.แพร่)",
      //       error: "Network Error",
      //     },
      //     {
      //       CONTNO: "V-001077",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 900 บาท(พบทรัพย์ นส.3ก.15 ต.แม่คำมี อ.หนองม่วงไข่ จ.แพร่)",
      //       error: "Request timeout",
      //     },
      //     {
      //       CONTNO: "8-0004959",
      //       MEMO1: "ค่าสืบทรัพย์ 1,720 บาท(ไม่พบทรัพย์)",
      //       error: "500 Internal Server Error",
      //     },
      //     {
      //       CONTNO: "2-0007973",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 690 บาท(พบทรัพย์ ฉ.36031 ต.ร้องกวาง อ.ร้องกวาง จ.แพร่)",
      //       error: "Network Error",
      //     },
      //     {
      //       CONTNO: "V-001077",
      //       MEMO1:
      //         "ค่าสืบทรัพย์ 900 บาท(พบทรัพย์ นส.3ก.15 ต.แม่คำมี อ.หนองม่วงไข่ จ.แพร่)",
      //       error: "Request timeout",
      //     },
      //     {
      //       CONTNO: "8-0004959",
      //       MEMO1: "ค่าสืบทรัพย์ 1,720 บาท(ไม่พบทรัพย์)",
      //       error: "500 Internal Server Error",
      //     },
      //   ],
      // };

      if (failed.length > 0) {
        message.warning(
          `สำเร็จ ${success.length} / ล้มเหลว ${failed.length} รายการ`,
        );
        setFailedModal({ open: true, items: failed }); // ✅ เปิด modal
      } else {
        if (userCompany !== "3") {
          message.success(
            `บันทึกหมายเหตุสำเร็จทั้งหมด ${success.length} รายการ`,
          );
        }
        func?.(true, preData?.[0]?.roundId);
        handleCancel();
      }
    } catch (e) {
      console.error("handleSave error:", e);
      // message.error("บันทึกล้มเหลว");
    } finally {
      setLoading(false);
    }
  };

  const preDataMemo = (data) => {
    // Group by contnoGroup
    const grouped = data.reduce((acc, item) => {
      const contno = item.contnoGroup;
      if (!acc[contno]) {
        acc[contno] = [];
      }
      acc[contno].push(item);
      return acc;
    }, {});

    // Process each contract group
    const result = Object.entries(grouped).map(([contno, items]) => {
      let memo = "";

      // คำนวณค่าสืบทรัพย์รวม (operationFee)
      const totalOperationFee = items.reduce(
        (sum, item) => sum + item.moneyOnline.operationFee,
        0,
      );
      memo += `ค่าสืบทรัพย์ ${totalOperationFee.toLocaleString()} บาท`;

      // รวบรวมทรัพย์สินทั้งหมด พร้อมข้อมูลเจ้าของ
      console.log("items", items);

      const allAssetsWithOwner = items
        .filter((item) => item.investigation && item.assetLand.length > 0)
        .map((item) => ({
          owner: `${item.relation === "-" ? "" : `${item.relation}`}(จล${item.garno}.`,
          assets: item.assetLand,
        }));

      if (allAssetsWithOwner.length > 0) {
        memo += "(พบทรัพย์ ";

        allAssetsWithOwner.forEach((ownerGroup, ownerIndex) => {
          // แสดงชื่อเจ้าของ
          if (ownerIndex > 0) {
            memo += " ";
          }
          memo += `${ownerGroup.owner} `;

          // Group assets by typeLand
          const assetsByType = ownerGroup.assets.reduce((acc, asset) => {
            const type = asset.typeLand;
            if (!acc[type]) {
              acc[type] = [];
            }
            acc[type].push(asset);
            return acc;
          }, {});

          // แสดงทรัพย์สินแต่ละประเภท
          // ✅ ไม่ต้องมี forEach ข้างนอกแล้ว
          const allAssetsWithPrefix = Object.entries(assetsByType).flatMap(
            ([type, assets]) =>
              assets.map((asset) => ({
                ...asset,
                typePrefix:
                  type === "1"
                    ? "ฉ."
                    : type === "2"
                      ? "นส.3ก."
                      : type === "3"
                        ? "ห้องชุด"
                        : type === "4"
                          ? "อาคารชุด"
                          : type === "5"
                            ? "นภ.1"
                            : "อื่น ๆ",
              })),
          );

          const locationGroups = {};

          allAssetsWithPrefix.forEach((asset) => {
            const locationKey = [
              asset.subdistrictName || "",
              asset.districtName || "",
              asset.provinceName || "",
            ].join("|");

            if (!locationGroups[locationKey]) {
              locationGroups[locationKey] = { assets: [], ref: asset };
            }
            locationGroups[locationKey].assets.push(asset);
          });

          const groupTexts = Object.values(locationGroups).map(
            ({ assets, ref }) => {
              const byType = {};
              assets.forEach((a) => {
                if (!byType[a.typePrefix]) byType[a.typePrefix] = [];
                byType[a.typePrefix].push(a);
              });

              const numberParts = Object.entries(byType).map(
                ([prefix, grpAssets]) =>
                  grpAssets
                    .map((a, i) =>
                      i === 0 ? `${prefix}${a.numberLand}` : `${a.numberLand}`,
                    )
                    .join(", "),
              );

              const numbers = numberParts.join(", ");

              const location = [];
              if (ref.subdistrictName)
                location.push(`ต.${ref.subdistrictName}`);
              if (ref.districtName)
                location.push(
                  `อ.${ref.districtName.replace("อำเภอ", "").trim()}`,
                );
              if (ref.provinceName) location.push(`จ.${ref.provinceName}`);

              return location.length > 0
                ? `${numbers} ${location.join(" ")}`
                : numbers;
            },
          );

          memo += groupTexts.join(", ");
          memo += ")";
        });
      } else {
        memo += "(ไม่พบทรัพย์) ";
      }
      memo += ")\n";
      // ค่าคัด ทร.14
      const totalTr14 = items.reduce(
        (sum, item) => sum + item.moneyOnline.tr14,
        0,
      );
      if (totalTr14 > 0) {
        memo += `ค่าคัด ทร.14 ${totalTr14} บาท \n`;
      }

      // ค่าคัดทะเบียนครอบครัว
      const totalFamilyReg = items.reduce(
        (sum, item) => sum + item.moneyOnline.familyRegistration,
        0,
      );
      if (totalFamilyReg > 0) {
        memo += `ค่าคัดทะเบียนครอบครัว ${totalFamilyReg} บาท \n`;
      }

      // ค่าคู่สมรส
      const totalSpouseFee = items.reduce(
        (sum, item) => sum + item.moneyOnline.spouseFee,
        0,
      );
      if (totalSpouseFee > 0) {
        memo += `ค่าคู่สมรส ${totalSpouseFee} บาท \n`;
      }

      // ค่าตรวจเพิ่ม
      const totalAdditionalFee = items.reduce(
        (sum, item) => sum + item.moneyOnline.additionalFee,
        0,
      );
      if (totalAdditionalFee > 0) {
        memo += `ค่าตรวจเพิ่ม ${totalAdditionalFee} บาท \n`;
      }
      memo += `${dayjs().format("DD/MM/YY")} /${USERNAME}`;

      // รวมทั้งหมด
      const grandTotal = items.reduce(
        (sum, item) => sum + item.moneyOnline.total,
        0,
      );

      return {
        DATA_TYPE: items[0].SCHEMA,
        CONTNO: contno,
        MEMO1: memo.trim(),
        TOTAL: grandTotal,
      };
    });

    return result;
  };

  // ===================== ส่งข้อมูลไป backend  =====================
  const postData = async (rawData) => {
    console.log("postData");

    setLoading(true);
    try {
      const res = await axios.post(
        BASE_URL_BEN + POST_SEND_RESPONE_INVESTIGATE,
        rawData,
        { headers: HEADERS_EXPORT_BEN },
      );
      const { status, data } = res;
      if (status === 200) {
        message.success(`บันทึกสำเร็จทรัพย์`);
      }
      return data;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) message.error("ไม่สามารถบันทึกในโปรแกรม Smart Link");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500)
        message.error("เคยนำข้อมูลเข้าแล้วหรือข้อมูลบัตรซ้ำกัน !");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );

      console.error("postData error:", error);
      throw error;
    }
  };

  const postMemoSequential = async (memoList, delayMs) => {
    const results = { success: [], failed: [] };

    for (const [index, item] of memoList.entries()) {
      try {
        await postMemo(item);
        results.success.push(item.CONTNO);
        console.log(`✅ [${index + 1}/${memoList.length}] ${item.CONTNO}`);
      } catch (error) {
        results.failed.push({
          CONTNO: item.CONTNO,
          MEMO1: item.MEMO1 || "-",
          error: error.message,
        });
        console.warn(
          `❌ [${index + 1}/${memoList.length}] ${item.CONTNO}`,
          error,
        );
      }

      const isLast = index === memoList.length - 1;
      if (!isLast) await new Promise((r) => setTimeout(r, delayMs));
    }

    return results;
  };

  const postMemo = async (rawData) => {
    console.log("postMemo", rawData);

    setLoading(true);
    try {
      const res = await axios.post(POST_UPDATE_EXCEL_MEMO, rawData, {
        headers: HEADERS_EXPORT_BEN,
      });
      const { status, data } = res;
      if (status === 200) {
        message.success(`บันทึกหมายเหตุสำเร็จ`);
      }
      return data;
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) message.error("ข้อมูลที่จะบันทึกล็อกหน้าจอมากเกินไป");
      else if (status === 400) message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      else if (status === 500)
        message.error("เคยนำข้อมูลเข้าแล้วหรือข้อมูลบัตรซ้ำกัน !");
      else
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );

      console.error("postData error:", error);
      throw error;
    }
  };

  // ===================== Items สำหรับ Collapse =====================
  const items = preData.map((item, idx) => {
    const itemKey = item.key; // ใช้ key เป็นหลัก
    const isInvalid = item.customerId === 0;
    const hasErrors = item.errors?.length > 0;
    const isChanged = changedKeys.has(item.key);
    const isChangedRelation = changedKeysRelation.has(item.key);
    const { isValid: isAssetValid, assetDetail } = checkAssetByIndex(
      preData,
      idx,
    );
    // เก็บ owner ของ panel เพื่อนำไปใช้ใน handleAssetEdit
    panelMapRef.current[itemKey] = item;

    const valids = preData.filter(
      (x) => x.customerId > 0 && x.contnoGroup === item.contnoGroup,
    );
    const selectOptions = valids.map((v) => ({
      label: `${fullName(v)} (${v.contnoGroup})`,
      value: v.customerId,
      data: v,
    }));

    const picked = selectedMap[itemKey];
    console.log("valids", valids);

    return {
      key: itemKey,
      label: (
        <div
          onClick={() => toggleItem(itemKey)}
          style={{ cursor: "pointer" }}
          role="button"
        >
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space direction="vertical" size={5}>
              {hasErrors && (
                <Tag color="volcano" style={{ fontSize: 12 }}>
                  <strong>⚠️ พบข้อมูลผิดพลาด</strong>
                </Tag>
              )}
              {isChanged && (
                <Tag
                  color={isAssetValid ? "green" : "red"}
                  style={{ fontSize: 12 }}
                >
                  <strong>✏️ มีการแก้ไขทรัพย์</strong>
                </Tag>
              )}
              {isChangedRelation && (
                <Tag color="blue" style={{ fontSize: 12 }}>
                  <strong>👨‍👩‍👧‍👦 มีการแก้ไขความสัมพันธ์</strong>
                </Tag>
              )}
              <Space direction="horizontal" size={10} align="center">
                {/* ทำกรอบตัวเลขให้เป็นวงกลมสวยๆ */}
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: "bold",

                    // --- ส่วนที่ปรับแต่งสีแบบ Inline ---
                    backgroundColor: hasErrors
                      ? "#fff2f0"
                      : isInvalid || isChanged
                        ? "#e6f4ff"
                        : "#f6ffed",

                    border: `1px solid ${
                      hasErrors
                        ? "#ffccc7"
                        : isInvalid || isChanged
                          ? "#91caff"
                          : "#b7eb8f"
                    }`,

                    color: hasErrors
                      ? "#ff4d4f"
                      : isInvalid || isChanged
                        ? "#1677ff"
                        : "#52c41a",
                  }}
                >
                  {item.noExcel}
                </div>
                <Tag
                  // 1. เปลี่ยนไปใช้ Semantic Color ของ Ant Design สีจะดูละมุนและเข้าตีมกว่า
                  color={
                    hasErrors
                      ? "error"
                      : isInvalid || isChanged
                        ? "processing"
                        : "success"
                  }
                  style={{
                    fontSize: 12,
                    fontWeight: 600, // 2. ย้ายความหนามาไว้ตรงนี้แทนการใช้แท็ก <strong>
                    padding: "2px 8px",
                    borderRadius: 6,
                  }}
                >
                  <Text
                    // 3. กำหนดค่า text ที่จะ copy ให้ชัดเจน ป้องกันการก๊อปปี้ space ส่วนเกิน
                    copyable={{ text: item.contnoGroup }}
                    // 4. บังคับให้ Text ดึงสีตัวอักษรมาจาก Tag (มิฉะนั้น Text จะกลายเป็นสีเทา/ดำ)
                    style={{ color: "inherit", margin: 0 }}
                  >
                    {item.contnoGroup}
                  </Text>
                </Tag>
              </Space>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {fullName(item)}
                {item.relationCustomerId ? `(${item.relation})` : null}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>
                จำเลยที่: {item.garno}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>
                เลขบัตร: {item.CUSCOD}
              </div>
            </Space>

            <Space direction="vertical" size={0} align="end">
              <Tag
                color={item.assetLand.length > 0 ? "green" : "orange"}
                style={{ fontSize: 12 }}
              >
                {item.assetLand.length > 0 ? "✓ พบทรัพย์" : "✗ ไม่พบทรัพย์"}
              </Tag>

              {isInvalid && valids.length > 0 && (
                <>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={false}
                    placeholder="เลือกบุคคลอ้างอิง..."
                    style={{ width: 260, marginTop: 8 }}
                    options={selectOptions}
                    value={
                      picked?.customerId ||
                      (item.relationCustomerId
                        ? valids.find(
                            (v) => v.customerId === item.relationCustomerId,
                          )?.customerId
                        : null) ||
                      null
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(value) => {
                      const selectedData = selectOptions.find(
                        (opt) => opt.value === value,
                      )?.data;

                      setPreData((prev) =>
                        prev.map((itm) =>
                          itm.key === item.key
                            ? {
                                ...itm,
                                relationCustomerId: value,
                                relation: itm.relation || "",
                              }
                            : itm,
                        ),
                      );

                      setSelectedMap((prev) => ({
                        ...prev,
                        [itemKey]: {
                          customerId: value,
                          data: selectedData,
                          relation: picked?.relation || item.relation || "",
                        },
                      }));

                      setChangedKeysRelation(
                        (prev) => new Set([...prev, item.key]),
                      );
                    }}
                  />
                  <Input
                    placeholder="ความสัมพันธ์ (เช่น บุตร, คู่สมรส)"
                    style={{ width: 260, marginTop: 8 }}
                    value={picked?.relation || item.relation || ""}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setPreData((prev) =>
                        prev.map((itm) =>
                          itm.key === item.key
                            ? { ...itm, relation: e.target.value }
                            : itm,
                        ),
                      );

                      setSelectedMap((prev) => ({
                        ...prev,
                        [itemKey]: {
                          ...prev[itemKey],
                          relation: e.target.value,
                        },
                      }));

                      setChangedKeysRelation(
                        (prev) => new Set([...prev, item.key]),
                      );
                    }}
                  />
                </>
              )}
            </Space>
          </Space>
        </div>
      ),
      children: (
        <Card
          title="รายละเอียด"
          size="small"
          style={{ marginBottom: 5 }}
          styles={{
            header: {
              background: !isAssetValid ? "#fff1f0" : "#f6ffed",
              borderBottom: `2px solid ${
                !isAssetValid ? "#ffccc7" : "#b7eb8f"
              }`,
            },
          }}
        >
          <div style={{ marginTop: 16 }}>
            {item.assetLand.length > 0 && (
              <>
                <Typography.Text strong>
                  ทรัพย์ที่พบ ({item.assetLand.length} แปลง)
                </Typography.Text>
                <Table
                  dataSource={item.assetLand}
                  columns={makeAssetColumns(itemKey)}
                  rowKey={(row) => row.key} // ใช้ key ของ assetLand เสมอ
                  pagination={false}
                  size="small"
                  style={{ marginTop: 8 }}
                  bordered
                />
              </>
            )}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleAddAssetRow(itemKey)}
              style={{ marginTop: 8, width: "100%" }}
            >
              เพิ่มทรัพย์
            </Button>
          </div>
        </Card>
      ),
    };
  });

  const renderFailedModal = () => {
    return (
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
            <span>
              รายการที่บันทึกไม่สำเร็จ ({failedModal.items.length} รายการ)
            </span>
          </Space>
        }
        open={failedModal.open}
        onCancel={() => {
          window.location.reload();
        }}
        footer={false}
        width={650}
        centered
        maskClosable={false}
      >
        <List
          size="small"
          dataSource={failedModal.items}
          style={{ maxHeight: 400, overflowY: "auto" }}
          renderItem={(item, i) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Avatar
                    size="small"
                    style={{ backgroundColor: "#ff4d4f", fontSize: 12 }}
                  >
                    {i + 1}
                  </Avatar>
                }
                title={
                  <Space>
                    <Tag color="red">{item.CONTNO}</Tag>
                  </Space>
                }
                description={
                  <>
                    <Card>
                      <Text type="secondary" style={{ fontSize: 14 }} copyable>
                        {item.MEMO1}
                      </Text>
                    </Card>
                    <Collapse
                      size="small"
                      style={{ marginTop: 6 }}
                      items={[
                        {
                          key: "1",
                          label: (
                            <Text type="danger" style={{ fontSize: 12 }}>
                              เนื่องจาก
                            </Text>
                          ),
                          children: (
                            <Text copyable style={{ fontSize: 12 }}>
                              {item.error}
                            </Text>
                          ),
                        },
                      ]}
                    />
                  </>
                }
              />
            </List.Item>
          )}
        />
        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text type="secondary" style={{ fontSize: "14px" }}>
            {"* ข้อมูลอาจมีความยาวมากเกินไปโปรดแก้ไขและบันทึกใหม่"}{" "}
            <span
              onClick={() =>
                window.open(
                  "/pre-investigate-assets/main-assets-online",
                  "_blank",
                )
              }
              style={{ color: "#1890ff", cursor: "pointer" }}
            >
              คลิก!
            </span>
          </Text>
          <Popconfirm
            placement="topLeft"
            title="ปิดหน้าต่างบันทึกล็อกหน้าจอล้มเหลว"
            description="คุณต้องการที่จะปิดข้อมูลที่บันทึกล้มเหลวใช่หรือไม่ !"
            onConfirm={() => window.location.reload()}
            onCancel={null}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
            okButtonProps={{ size: "large" }}
            cancelButtonProps={{ size: "large" }}
          >
            <Button type="primary">ปิด</Button>
          </Popconfirm>
        </div>
      </Modal>
    );
  };

  return (
    <Modal
      title="รายละเอียดการสืบทรัพย์"
      open={open}
      onCancel={handleCancel}
      width={1000}
      keyboard
      maskClosable={false}
      centered
      footer={[
        <div
          key="footer-container"
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 10,
            }}
          >
            {[
              {
                label: `ยอดจาก Excel ${sumMoney.totalSum === sumMoneyEstimate.totalSum ? "✅" : "⛔"}`,
                data: sumMoney,
                labelColor: "#0C447C",
                bgColor: "#E6F1FB",
                borderColor: "#85B7EB",
                valueColor: "#185FA5",
              },
              {
                label: "ยอดจากระบบ ✅",
                data: sumMoneyEstimate,
                labelColor: "#27500A",
                bgColor: "#EAF3DE",
                borderColor: "#97C459",
                valueColor: "#3B6D11",
              },
            ].map(
              ({
                label,
                data,
                labelColor,
                bgColor,
                borderColor,
                valueColor,
              }) => (
                <div
                  key={label}
                  style={{
                    background: bgColor,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 10,
                    padding: "10px 16px",
                  }}
                >
                  {/* Badge */}
                  <div style={{ marginBottom: 10 }}>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: labelColor,
                        background: "rgba(255,255,255,0.7)",
                        borderRadius: 20,
                        padding: "2px 12px",
                        border: `1px solid ${borderColor}`,
                      }}
                    >
                      {label}
                    </span>
                  </div>

                  {/* Items */}
                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {[
                      { label: "ทร.14", value: data?.tr14Sum },
                      {
                        label: "ทะเบียนครอบครัว",
                        value: data?.familyRegistrationSum,
                      },
                      { label: "ค่าดำเนินงาน", value: data?.operationFeeSum },
                      { label: "ค่าตรวจเพิ่ม", value: data?.additionalFeeSum },
                    ].map(({ label: lbl, value }) => (
                      <div key={lbl} style={{ fontSize: 13 }}>
                        <span style={{ color: labelColor, opacity: 0.8 }}>
                          {lbl}:{" "}
                        </span>
                        <span style={{ fontWeight: 600, color: valueColor }}>
                          {value?.toLocaleString() || "0"}
                        </span>
                      </div>
                    ))}

                    {/* รวม */}
                    <div
                      style={{
                        marginLeft: "auto",
                        paddingLeft: 16,
                        borderLeft: `2px solid ${borderColor}`,
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: labelColor, opacity: 0.8 }}>
                        รวม:{" "}
                      </span>
                      <span
                        style={{
                          fontWeight: 700,
                          color: valueColor,
                          fontSize: 15,
                        }}
                      >
                        {data?.totalSum?.toLocaleString() || "0"}
                      </span>
                      <span
                        style={{
                          color: labelColor,
                          opacity: 0.7,
                          marginLeft: 4,
                        }}
                      >
                        บาท
                      </span>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              key="back"
              danger
              onClick={handleCancel}
              style={{ minWidth: 80 }}
            >
              ยกเลิก
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleSave}
              style={{ minWidth: 100 }}
            >
              บันทึกข้อมูล
            </Button>
          </div>
        </div>,
      ]}
    >
      <div
        ref={scrollRef}
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          position: "relative",
          paddingRight: 8,
        }}
      >
        <Spin spinning={loading} size="large" tip="Loading...">
          {preData.length > 0 ? (
            <Collapse
              items={items}
              accordion
              activeKey={activeKey}
              onChange={setActiveKey}
            />
          ) : (
            <Empty description="ไม่มีข้อมูลที่ต้องเชื่อมโยง" />
          )}
        </Spin>

        <FloatButton.BackTop
          target={() => scrollRef.current}
          visibilityHeight={80}
          style={{ right: 16, bottom: 16 }}
        />
      </div>
      {renderFailedModal()}
    </Modal>
  );
};

export default AddRelationAssets;
